import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { redirect } from "react-router-dom";
import { ApiError } from "@/services/errors";
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { RefreshTokenResponse, RuntimeTokenReponse } from "./entities";
import { store } from "../store";
import {
  logout,
  setAppIsAuthenticated,
  setRefreshToken,
  setToken,
  setShouldEnterPin,
  setRuntimeToken,
} from "../auth/slice";
import { appRouterPaths } from "../navigation";

const baseUrl = window.__RUNTIME_CONFIG__.BASE_URL;

const prepareHeaders = (headers: Headers) => {
  const authToken = store.getState().auth.token;
  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
  }
  return headers;
};

export const baseQuery = fetchBaseQuery({
  baseUrl: `${baseUrl}/api/v1`,
  prepareHeaders,
});

export const baseQueryWithoutAuth = fetchBaseQuery({
  baseUrl: `${baseUrl}/api/v1`,
});

const refreshRuntimeToken = async ({
  api,
  extraOptions,
}: {
  api: BaseQueryApi;
  extraOptions: object;
}) => {
  const runtimeTokenResult = await baseQuery(
    {
      url: "/auth/runtimeToken",
      method: "GET",
    },
    api,
    extraOptions,
  );
  if (runtimeTokenResult?.data) {
    const runtimeToken = runtimeTokenResult.data as RuntimeTokenReponse;
    api.dispatch(setRuntimeToken(runtimeToken.runtimeToken));
  }
};

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshToken = store.getState().auth.refreshToken;

    const refreshResult = await baseQueryWithoutAuth(
      {
        url: "/auth/refresh-token",
        method: "POST",
        body: { refreshToken },
      },
      api,
      extraOptions,
    );

    if (!refreshResult.error && refreshResult.data) {
      const tokens = refreshResult.data as RefreshTokenResponse;
      api.dispatch(setToken(tokens.token));
      api.dispatch(setRefreshToken(tokens.refreshToken));
      refreshRuntimeToken({ api, extraOptions });
      api.dispatch(setAppIsAuthenticated(true));

      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
      redirect(appRouterPaths.logIn);
    }
  }

  if (result.error && (result.error.data as { errorCode: string })?.errorCode === "PA_003520") {
    api.dispatch(setShouldEnterPin(true));

    return result;
  }

  if (result.error && result.error.status !== 401) {
    throw new ApiError((result.error.data as { errorCode: string })?.errorCode);
  }

  return result;
};
